import { FieldVerification, FieldVerificationSort } from "models";

/**
 * Gets the verification status from an array of verifications
 * @param verifications - Array of verification objects
 * @returns Status: "rejected" | "pending" | "approved"
 */
export const getFieldVerificationStatus = (verifications: { verified: boolean | null }[] = []) => {
  if (verifications.some(({ verified }) => verified === false)) return "rejected";
  if (verifications.some(({ verified }) => verified === null)) return "pending";
  return "approved";
};

/**
 * Checks if a field number should be treated as "unknown"
 * Matches TableFieldPanel logic: field.number ? ... : "Field number unknown"
 */
const isFieldNumberUnknown = (number: string | undefined | null): boolean => !number;

/**
 * Extracts numeric value from field number string
 * @param number - Field number string (e.g., "4301" or "Field #4301")
 * @returns Numeric value or null if not found
 */
const extractFieldNumber = (number: string | undefined | null): number | null => {
  if (isFieldNumberUnknown(number)) return null;

  if (typeof number === "number") {
    return Number.isNaN(number) ? null : number;
  }

  if (typeof number !== "string") return null;

  const trimmed = number.trim();
  if (trimmed === "") return null;

  const lowerTrimmed = trimmed.toLowerCase();
  if (lowerTrimmed === "unknown" || lowerTrimmed === "null" || lowerTrimmed === "undefined" || lowerTrimmed === "none") {
    return null;
  }

  const match = trimmed.match(/\d+/);
  if (!match) return null;

  const parsed = parseInt(match[0], 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Sorts fields based on the selected sort option
 * @param fields - Array of fields to sort
 * @param sort - Sort configuration (sortBy and sortOrder)
 * @returns Sorted array of fields
 */
export const sortFields = (fields: FieldVerification[], sort: FieldVerificationSort | undefined): FieldVerification[] => {
  if (!sort) return fields;

  return [...fields].sort((a, b) => {
    if (!sort.sortBy) return 0;

    let comparison = 0;
    if (sort.sortBy === "name") {
      comparison = (a.name || "").localeCompare(b.name || "");
    } else if (sort.sortBy === "size") {
      comparison = a.size - b.size;
    } else if (sort.sortBy === "number") {
      const aNumber = extractFieldNumber(a.number);
      const bNumber = extractFieldNumber(b.number);
      const aHasNoNumber = aNumber === null;
      const bHasNoNumber = bNumber === null;

      if (aHasNoNumber && bHasNoNumber) {
        return 0;
      }
      if (aHasNoNumber) {
        return sort.sortOrder === "asc" ? -1 : 1;
      }
      if (bHasNoNumber) {
        return sort.sortOrder === "asc" ? 1 : -1;
      }

      comparison = aNumber - bNumber;
      return sort.sortOrder === "desc" ? -comparison : comparison;
    }

    return sort.sortOrder === "desc" ? -comparison : comparison;
  });
};

/**
 * Applies pagination to an array of items
 * @param items - Array of items to paginate
 * @param page - Current page number (1-indexed)
 * @param itemsPerPage - Number of items per page
 * @returns Paginated array of items
 */
export const paginateItems = <T>(items: T[], page: number, itemsPerPage: number): T[] => {
  const start = (page - 1) * itemsPerPage;
  return items.slice(start, start + itemsPerPage);
};
